import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ESTADO_ENVIO_COLOR, ESTADO_ENVIO_LABEL } from '@/lib/types';
import {
  useTablaPrefs,
  ResizeHandle,
  ColorMenu,
  colorPersonal,
  aplicarColor,
  type MenuColor,
} from '@/lib/uiPrefs';
import type { EstadoEnvio } from '@/lib/types';

/**
 * Reporte TEMPORAL "Operaciones (Excel)" (22-sep-2026): muestra tal cual lo
 * que los operativos capturan en sus Excel del Drive (staging), en paralelo
 * al Reporte de Operaciones actual, para que lo validen SIN tocar los datos
 * de producción. Una vez validado, se vuelca a producción y este reporte se
 * convierte en el definitivo. Solo lectura.
 */

interface FilaExcel {
  id: number;
  posicion: string;
  estadoNorm: string | null;
  bookingId: number | null;
  vista: string;
  archivo: string;
  agenteCarpeta: string;
  fila: number;
  operativoAtlas: string | null;
  [k: string]: unknown;
}

interface StaffInfo {
  iniciales: string;
  nombre: string;
}

interface Resumen {
  total: number;
  conMatch: number;
  sinMatch: number;
  posicionesSinMatch: { posicion: string; archivo: string; fila: number }[];
  operativos: string[];
  estados: { estado: string; count: number }[];
  archivos: string[];
  importadoAt: string | null;
}

type ColX = { key: string; label: string; titulo?: string };

const COLS_MAR_AER: ColX[] = [
  { key: 'agenteCarpeta', label: 'OP', titulo: 'Operativo (carpeta del Drive)' },
  { key: 'posicion', label: 'Posición' },
  { key: 'cnee', label: 'Cnee' },
  { key: 'shipper', label: 'Shipper' },
  { key: 'master', label: 'MBL/MAWB' },
  { key: 'houseRef', label: 'HBL/HAWB' },
  { key: 'origenPuerto', label: 'POL/AOL' },
  { key: 'destinoPuerto', label: 'POD/AOD' },
  { key: 'etd', label: 'ETD' },
  { key: 'eta', label: 'ETA' },
  { key: 'container', label: 'Container' },
  { key: 'linea', label: 'Line' },
  { key: 'vesselFlight', label: 'Vessel/Flight' },
  { key: 'venta', label: 'Venta' },
  { key: 'costo', label: 'Costo' },
  { key: 'profit', label: 'Profit' },
  { key: 'facturacion', label: 'Facturación' },
  { key: 'bl', label: 'BL' },
  { key: 'locales', label: 'Locales' },
  { key: 'agente', label: 'Agente' },
  { key: 'inland', label: 'Inland' },
  { key: 'caat', label: 'CAAT' },
  { key: 'seguro', label: 'Seguro' },
  { key: 'otro', label: 'Otro' },
  { key: 'liberacion', label: 'Liberación' },
  { key: 'aa', label: 'AA' },
  { key: 'encomienda', label: 'Encomienda' },
  { key: 'garantia', label: 'Garantía' },
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'demoras', label: 'Demoras' },
  { key: 'cerrado', label: 'Cerrado' },
  { key: 'pagosNav', label: 'Pagos Nav/TLX' },
  { key: 'comments', label: 'Comments' },
];

const COLS_OTR: ColX[] = [
  { key: 'agenteCarpeta', label: 'OP', titulo: 'Operativo (carpeta del Drive)' },
  { key: 'posicion', label: 'Posición' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'master', label: 'BOL/PRO' },
  { key: 'status', label: 'Status' },
  { key: 'origenPuerto', label: 'Origin' },
  { key: 'destinoPuerto', label: 'Destination' },
  { key: 'etd', label: 'PU/ETD' },
  { key: 'eta', label: 'ETA' },
  { key: 'invoicePo', label: 'Invoice/PO' },
  { key: 'pieces', label: 'Pieces' },
  { key: 'weight', label: 'Weight' },
  { key: 'cajaUnidad', label: 'Caja/Unidad' },
  { key: 'usa', label: 'USA' },
  { key: 'transfer', label: 'Transfer' },
  { key: 'mex', label: 'MEX' },
  { key: 'facturacion', label: 'Facturación' },
  { key: 'inlandMx', label: 'Inland MX' },
  { key: 'transfer2', label: 'Transfer (USA)' },
  { key: 'inlandUsa', label: 'Inland USA' },
  { key: 'locales', label: 'Locales' },
  { key: 'seguro', label: 'Seguro' },
  { key: 'otro', label: 'Otro' },
  { key: 'aa', label: 'AA' },
  { key: 'cerrado', label: 'Cerrado' },
  { key: 'comments', label: 'Comments' },
];

/** Etiquetas y colores extra para los grupos que no existen en el board */
const LABEL_EXTRA: Record<string, string> = {
  entregado: 'Entregado',
  'en jrb': 'En JRB',
  otro: 'Otro',
  'sin status': 'Sin status',
  cancelado: 'Cancelado',
};
const COLOR_EXTRA: Record<string, { bg: string; text: string }> = {
  entregado: { bg: '#C6E0B4', text: '#1a2e05' }, // verde suave
  'en jrb': { bg: '#DDEBF7', text: '#1e3a5f' }, // azul suave
};

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function fmtDia(v: unknown): string {
  const m = String(v ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[3])}-${MESES[Number(m[2]) - 1]}-${m[1]}` : String(v ?? '');
}

/** Celda abierta del reporte Excel: click para editar, Enter/blur guarda,
 *  Esc cancela. Editar marca la fila como "editada en Atlas" (el re-import
 *  del Drive ya no la pisa). */
function CeldaX({
  fila,
  campo,
  editable,
  display,
}: {
  fila: FilaExcel;
  campo: string;
  editable: boolean;
  display: string;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState('');
  const mut = useMutation({
    mutationFn: (v: string) =>
      api(`/reports/operaciones-excel/${fila.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [campo]: v }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reporte-operaciones-excel'] }),
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar'),
  });
  const actual = fila[campo] == null ? '' : String(fila[campo]);
  if (!editing) {
    return (
      <div
        className={cn(
          'min-h-[1.4rem] truncate',
          editable && 'cursor-text hover:outline hover:outline-1 hover:outline-amber-500 rounded',
        )}
        title={display || (editable ? 'Click para editar' : undefined)}
        onClick={() => {
          if (!editable) return;
          setValor(actual);
          setEditing(true);
        }}
      >
        {display || (editable ? <span className="text-ink-300">—</span> : '')}
      </div>
    );
  }
  const guardar = () => {
    setEditing(false);
    if (valor !== actual) mut.mutate(valor);
  };
  return (
    <input
      autoFocus
      className="w-full min-w-[6rem] px-1 py-0.5 text-sm border border-brand-400 rounded bg-surface-1 text-ink-900"
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') guardar();
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

export function OperacionesExcelPage() {
  const { user } = useAuth();
  const [vista, setVista] = useState<'maritimo-aereo' | 'otr'>('maritimo-aereo');
  const [search, setSearch] = useState('');
  const [operativo, setOperativo] = useState<string | undefined>(undefined);
  const [estado, setEstado] = useState<string | undefined>(undefined);
  const [verSinMatch, setVerSinMatch] = useState(false);
  // personalización por usuario: anchos + colores (solo los ve él)
  const [uiPrefs, setUiPrefs] = useTablaPrefs('reporte-operaciones-excel');
  const [menuColor, setMenuColor] = useState<MenuColor | null>(null);
  const setW = (k: string, w: number) => setUiPrefs((p) => ({ ...p, w: { ...p.w, [k]: w } }));

  const q = useQuery({
    queryKey: ['reporte-operaciones-excel', vista, search, operativo, estado],
    queryFn: () =>
      api<{ items: FilaExcel[]; resumen: Resumen; staffPorCarpeta: Record<string, StaffInfo> }>('/reports/operaciones-excel', {
        params: { vista, search: search || undefined, operativo, estado },
      }),
  });

  const cols = vista === 'otr' ? COLS_OTR : COLS_MAR_AER;
  const items = q.data?.items ?? [];
  const r = q.data?.resumen;
  const staffMap = q.data?.staffPorCarpeta ?? {};
  // el servidor valida a detalle; aquí solo se decide si la celda se ve editable
  const editable =
    user?.rol === 'admin' || user?.rol === 'director' || user?.rol === 'agente' || user?.rol === 'gerente';

  // Columnas fijas (igual que el reporte principal): Posición y Cliente para
  // todos; para el gerente (Thalía) también la de agente/carpeta. Offsets
  // medidos en vivo porque todas son redimensionables.
  const colKeys = new Set(cols.map((c) => c.key));
  const stickyKeys = [
    ...(user?.rol === 'gerente' || user?.rol === 'director' ? ['agenteCarpeta'] : []),
    'posicion',
    // mar/aér no tiene 'Cliente' tal cual: se fija Cnee en su lugar
    ...(colKeys.has('cliente') ? ['cliente'] : colKeys.has('cnee') ? ['cnee'] : []),
  ].filter((k) => colKeys.has(k));
  const stickyRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [stickyW, setStickyW] = useState<Record<string, number>>({});
  useEffect(() => {
    const medir = () => {
      const w: Record<string, number> = {};
      for (const k of stickyKeys) {
        const el = stickyRefs.current[k];
        if (el) w[k] = el.getBoundingClientRect().width;
      }
      setStickyW((prev) =>
        stickyKeys.every((k) => Math.abs((prev[k] ?? 0) - (w[k] ?? 0)) < 0.5) ? prev : w,
      );
    };
    medir();
    const obs = new ResizeObserver(medir);
    for (const k of stickyKeys) {
      const el = stickyRefs.current[k];
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyKeys.join(','), vista]);
  const stickyLeft: Record<string, number> = {};
  let stickyTotal = 0;
  {
    const DEF: Record<string, number> = { agenteCarpeta: 60, posicion: 110, cliente: 100, cnee: 100 };
    let acc = 0;
    for (const k of stickyKeys) {
      stickyLeft[k] = acc;
      acc += stickyW[k] ?? DEF[k] ?? 100;
    }
    stickyTotal = acc;
  }
  const ultimaSticky = stickyKeys[stickyKeys.length - 1];
  // borde distintivo de las columnas fijas (para diferenciarlas al scrollear)
  const bordeSticky = (k: string): React.CSSProperties => ({
    borderColor: '#d9958f',
    ...(k === ultimaSticky ? { borderRight: '2px solid #c8564d' } : {}),
  });

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 w-[95%] max-w-none mx-auto">
      <style>{`
        .reportx-scroll { overflow: scroll; max-height: 72vh; }
        .reportx-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
        .reportx-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
        .reportx-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 7px; border: 3px solid #f1f5f9; }
      `}</style>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold">
            Reportes · Operaciones (Excel){' '}
            <span className="text-xs font-normal px-2 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
              temporal · en validación
            </span>
          </h1>
          <p className="text-sm text-ink-400">
            Lo que los operativos capturan en sus Excel del Drive — aquí ya se puede editar
            directo (click en la celda); lo editado en Atlas manda sobre el Excel.
            {r?.importadoAt && <> · Importado: {fmtDia(r.importadoAt)}</>}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-surface-2">
        <div className="flex rounded-md border border-ink-200 overflow-hidden">
          {(
            [
              ['maritimo-aereo', 'Marítimo / Aéreo'],
              ['otr', 'OTR (Terrestre)'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setVista(v)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium',
                vista === v ? 'bg-brand-500 text-white' : 'bg-surface-1 text-ink-500 hover:bg-surface-2',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <Input
          placeholder="Buscar posición, cliente, shipper…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
        />
        {(r?.operativos.length ?? 0) > 1 && (
          <Select
            value={operativo ?? '__all__'}
            onValueChange={(v) => setOperativo(v === '__all__' ? undefined : v)}
          >
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Operativo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los operativos</SelectItem>
              {(r?.operativos ?? []).map((op) => (
                <SelectItem key={op} value={op}>
                  {op}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          value={estado ?? '__all__'}
          onValueChange={(v) => setEstado(v === '__all__' ? undefined : v)}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los status</SelectItem>
            {(r?.estados ?? []).map((e) => (
              <SelectItem key={e.estado} value={e.estado}>
                {ESTADO_ENVIO_LABEL[e.estado as EstadoEnvio] ?? LABEL_EXTRA[e.estado] ?? e.estado} ({e.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {r && (
          <div className="text-sm text-ink-500 flex items-center gap-3 ml-auto">
            <span>
              <b>{r.total}</b> filas · <b className="text-green-600">{r.conMatch}</b> cruzan con Atlas
            </span>
            {r.sinMatch > 0 && (
              <button
                className="inline-flex items-center gap-1 text-amber-700 hover:underline"
                onClick={() => setVerSinMatch(!verSinMatch)}
              >
                <AlertTriangle className="h-4 w-4" />
                {r.sinMatch} sin match {verSinMatch ? '▴' : '▾'}
              </button>
            )}
          </div>
        )}
      </div>

      {verSinMatch && r && r.posicionesSinMatch.length > 0 && (
        <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm">
          <p className="font-medium text-amber-800 mb-1">
            Posiciones del Excel que no existen en Atlas (no se crean; se listan para decidir):
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-amber-900">
            {r.posicionesSinMatch.map((p, i) => (
              <span key={i} className="font-mono text-xs">
                {p.posicion} <span className="text-amber-600">({p.archivo}, fila {p.fila})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div
        className="reportx-scroll rounded-lg border border-ink-200 bg-surface-1"
        style={{ scrollSnapType: 'x proximity', scrollPaddingLeft: `${stickyTotal}px` }}
      >
        <table
          className="text-sm"
          style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <thead className="sticky top-0 z-10">
            <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              {cols.map((c) => {
                const esSticky = c.key in stickyLeft;
                return (
                  <th
                    key={c.key}
                    ref={(el) => {
                      if (esSticky) stickyRefs.current[c.key] = el;
                    }}
                    className={cn(
                      'px-2 py-2 text-left font-medium border-b border-r border-ink-100 whitespace-nowrap',
                      esSticky && 'bg-surface-2',
                    )}
                    style={{
                      overflow: 'hidden',
                      position: 'relative',
                      ...(uiPrefs.w[c.key]
                        ? { width: uiPrefs.w[c.key], minWidth: uiPrefs.w[c.key], maxWidth: uiPrefs.w[c.key] }
                        : { minWidth: '2rem', maxWidth: '40rem' }),
                      ...(esSticky
                        ? { position: 'sticky', left: `${stickyLeft[c.key]}px`, zIndex: 20, ...bordeSticky(c.key) }
                        : { scrollSnapAlign: 'start' }),
                    }}
                    title={c.titulo ?? c.label}
                  >
                    {c.label}
                    <ResizeHandle colKey={c.key} setW={setW} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {items.map((f) => {
              const color = f.estadoNorm
                ? ESTADO_ENVIO_COLOR[f.estadoNorm as EstadoEnvio] ?? COLOR_EXTRA[f.estadoNorm]
                : undefined;
              // fondo claro SIEMPRE con letra oscura explícita (en modo oscuro
              // la letra del tema es clara y no se distingue)
              const estilo = color
                ? { backgroundColor: color.bg, color: color.text }
                : f.estadoNorm === 'cancelado'
                  ? { backgroundColor: '#FEE2E2', color: '#7F1D1D' }
                  : !f.bookingId
                    ? { backgroundColor: '#FEF3C7', color: '#713F12' }
                    : undefined;
              return (
              <tr
                key={f.id}
                style={estilo}
                className={cn(
                  !f.bookingId && 'outline outline-1 -outline-offset-1 outline-amber-400',
                  f.estadoNorm === 'cancelado' && 'line-through',
                )}
                title={
                  f.bookingId
                    ? `${f.archivo} · fila ${f.fila}${f.operativoAtlas ? ` · operativo en Atlas: ${f.operativoAtlas}` : ''}`
                    : `${f.archivo} · fila ${f.fila} · posición NO encontrada en Atlas`
                }
              >
                {cols.map((c) => {
                  // tal cual su Excel: sin reformatear nada
                  const v = String(f[c.key] ?? '');
                  const rowKey = `${f.agenteCarpeta}|${f.posicion}`;
                  const miColor = colorPersonal(uiPrefs, rowKey, c.key);
                  const anchoTd = uiPrefs.w[c.key]
                    ? { maxWidth: uiPrefs.w[c.key], overflow: 'hidden' as const }
                    : undefined;
                  return (
                    <td
                      key={c.key}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setMenuColor({ x: e.clientX, y: e.clientY, rowKey, colKey: c.key });
                      }}
                      style={
                        c.key in stickyLeft
                          ? {
                              position: 'sticky',
                              left: `${stickyLeft[c.key]}px`,
                              zIndex: 5,
                              backgroundColor: miColor ?? estilo?.backgroundColor,
                              color: miColor ? '#1f2937' : estilo?.color,
                              ...bordeSticky(c.key),
                              ...anchoTd,
                            }
                          : {
                              ...(miColor ? { backgroundColor: miColor, color: '#1f2937' } : {}),
                              ...anchoTd,
                            }
                      }
                      className={cn(
                        'px-1.5 py-1 border-b border-r border-ink-100 align-top whitespace-nowrap',
                        c.key in stickyLeft && !estilo && 'bg-surface-1',
                        c.key === 'posicion' && 'font-medium tabular',
                        // columnas largas: a la mitad por defecto (como el
                        // reporte original); el th es redimensionable
                        ['cliente', 'shipper', 'cnee', 'linea'].includes(c.key) &&
                          'max-w-[10rem] overflow-hidden',
                        c.key === 'comments' && 'max-w-[12rem] overflow-hidden',
                      )}
                    >
                      {c.key === 'agenteCarpeta' ? (
                        // Iniciales oficiales de CW; nombre completo en hover
                        <div
                          className="truncate font-medium"
                          title={staffMap[v] ? `${staffMap[v].nombre} (${v})` : v || undefined}
                        >
                          {staffMap[v]?.iniciales ?? v}
                        </div>
                      ) : (
                        <CeldaX fila={f} campo={c.key} editable={editable} display={v} />
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            })}
            {items.length === 0 && !q.isLoading && (
              <tr>
                <td colSpan={cols.length} className="px-3 py-8 text-center text-ink-400">
                  Sin filas importadas para esta vista.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {menuColor && (
        <ColorMenu
          menu={menuColor}
          onPick={(scope, color) => setUiPrefs((p) => aplicarColor(p, menuColor, scope, color))}
          onClose={() => setMenuColor(null)}
        />
      )}
    </div>
  );
}
